import styled from 'styled-components';
import React, { useContext, useRef, useMemo, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { EditorContext } from '@/features/editor/context/editor-context';
import { useCatalogSearch } from '@/components/catalog-search/use-catalog-search';
import { CircularCheckmark } from '@/components/circular-checkmark';
import { GradientModal } from '@/components/gradient-modal';
import { centered } from '@/components/use-shared-element';
import { VSpace } from '@/components/spacing';
import { TabSelect } from '@/components/tab-select';
import { useMatchRules } from '@/features/editor/widgets/price-test/use-match-rules';
import { QueryBuilder } from '@/components/query-builder';
import { QBItemSelection } from '@/components/query-builder/models';
import { BigButton } from '@/components/big-button';
import { ExperienceSpinner } from '@/components/experience-spinner';

interface Props {
  isVisible: boolean;
  onClose: () => void;
}

const MODAL_RECT = centered(66, 65);
const TABS = [`MANUAL`, `BY RULE`];

export function ProductSelectorForAnalytics({ isVisible, onClose }: Props) {
  const {
    experienceState: { currentExperience, setProductsForReport },
  } = useContext(EditorContext);
  const fromRef = useRef<HTMLElement>(null);
  const scrollRef = useRef<HTMLElement>(null);
  const { searchText, setSearchText, products, hasMore, loadMore } =
    useCatalogSearch();

  const existingData = useMemo(
    () =>
      currentExperience?.listOfProductsForReport || {
        matchType: `manual`,
        manualProducts: [],
        rules: null,
      },
    [currentExperience, isVisible],
  );

  const [matchType, setMatchType] = useState<'manual' | 'rule'>(
    (existingData.matchType as 'manual' | 'rule') || `manual`,
  );

  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(
    new Set(existingData?.manualProducts?.map((p) => p.id) || []),
  );

  const [rules, setRules] = useState<QBItemSelection[]>(
    existingData?.rules?.rules || [],
  );

  React.useEffect(() => {
    if (isVisible) {
      const data = currentExperience?.listOfProductsForReport || {
        matchType: `manual`,
        manualProducts: [],
        rules: null,
      };
      setMatchType(data?.matchType || `manual`);
      setSelectedProductIds(
        new Set(data?.manualProducts?.map((p) => p.id) || []),
      );
      setRules(data?.rules?.rules || []);
    }
  }, [isVisible, currentExperience]);

  const mergedProducts = useMemo(() => {
    const out: ProductLineItem[] = [];
    const existingProducts = existingData?.manualProducts || [];

    if (!searchText && existingProducts.length > 0) {
      out.push(
        ...existingProducts.map((p) => ({
          id: p.id,
          handle: p.handle,
          title: p.title,
          image: p.image,
          price: p.price,
          isSelected: selectedProductIds.has(p.id),
        })),
      );
    }

    out.push(
      ...(products
        ?.filter((p) => !out.find((op) => p.id === op.id))
        .map((p) => ({
          id: p.id,
          handle: p.handle,
          title: p.title,
          image: p.image?.src,
          price: p.price,
          isSelected: selectedProductIds.has(p.id),
        })) || []),
    );

    return out;
  }, [products, existingData, selectedProductIds, searchText]);

  const toggleProduct = (product: ProductLineItem, selected: boolean) => {
    setSelectedProductIds((prev) => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(product.id);
      } else {
        newSet.delete(product.id);
      }
      return newSet;
    });
  };

  const handleTabChange = (idx: number) => {
    setMatchType(idx === 0 ? `manual` : `rule`);
  };

  const { condition } = useMatchRules();

  const onQueryBuilderChange = (value: QBItemSelection[]) => {
    setRules(value);
  };

  const handleSave = () => {
    if (matchType === `manual`) {
      const selectedProducts = mergedProducts
        .filter((p) => selectedProductIds.has(p.id))
        .map((p) => ({
          id: p.id,
          handle: p.handle,
          title: p.title,
          image: p.image,
          price: p.price,
          compareAtPrice: p.price,
          offsetType: `fixed` as const,
          priceOffset: 0,
        }));

      setProductsForReport({
        matchType: `manual`,
        manualProducts: selectedProducts,
        rules: null,
      });
    } else {
      setProductsForReport({
        matchType: `rule`,
        manualProducts: [],
        rules: {
          rules,
          offsetType: `fixed`,
          priceOffset: 0,
        },
      });
    }
    onClose();
  };

  const selectedCount =
    matchType === `manual` ? selectedProductIds.size : rules.length;

  return (
    <>
      <HiddenTrigger ref={fromRef} />
      <GradientModal
        showBackdrop
        isVisible={isVisible}
        targetPosAndSize={MODAL_RECT}
        fromRef={fromRef}
        scrollRef={scrollRef}
        onClose={onClose}
        fromStyle={{
          borderRadius: `1.5rem`,
          backgroundColor: `#DEDEDE`,
          opacity: `0`,
        }}
        toStyle={{
          borderRadius: `1.2rem`,
          backgroundColor: `#FFFFFF`,
          opacity: `1`,
        }}
        header={
          <HeaderWrapper>
            <HeaderTitle>Select Products for Analysis</HeaderTitle>
            <TabSelect
              tabs={TABS}
              tabHeight={2.8}
              containerPadding={0}
              tabWidth={12}
              tabFont="Inter"
              defaultValue={matchType === `manual` ? TABS[0] : TABS[1]}
              onTabSelected={handleTabChange}
            />
          </HeaderWrapper>
        }
        noScroll={false}
        footer={
          <Footer>
            <BigButton
              border="2px solid #ddd"
              background="white"
              color="#495057"
              noShadow
              noTransform
              size="medium"
              fillWidth
              onClick={onClose}
            >
              Cancel
            </BigButton>
            <BigButton
              border="none"
              background="black"
              color="white"
              noTransform
              size="medium"
              fillWidth
              onClick={handleSave}
            >
              Save ({selectedCount}) selected
            </BigButton>
          </Footer>
        }
      >
        <ContentWrapper>
          <VSpace value={5} />
          <Description>Choose products to analyze separately.</Description>
          <VSpace value={2} />

          {matchType === `manual` ? (
            <ProductsWrapper>
              <Search
                placeholder="Search for products"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
              <ProductsScrollContainer id="scrollableProducts">
                <InfiniteScroll
                  dataLength={mergedProducts?.length || 0}
                  next={() => {
                    loadMore();
                  }}
                  hasMore={hasMore}
                  loader={
                    <LoadingState>
                      <ExperienceSpinner
                        size={2.4}
                        thickness={0.25}
                        color="#6c757d"
                      />
                    </LoadingState>
                  }
                  scrollableTarget="scrollableProducts"
                >
                  {mergedProducts.map((p) => (
                    <ProductRow
                      key={p.id}
                      product={p}
                      onToggle={toggleProduct}
                    />
                  ))}
                </InfiniteScroll>
              </ProductsScrollContainer>
            </ProductsWrapper>
          ) : (
            <RulesWrapper>
              <QueryBuilder
                whereNotion="If"
                onChange={onQueryBuilderChange}
                initialProps={condition}
                initialState={rules}
              />
            </RulesWrapper>
          )}
        </ContentWrapper>
      </GradientModal>
    </>
  );
}

interface ProductLineItem {
  id: string;
  handle: string;
  title: string;
  image: string;
  price: number;
  isSelected: boolean;
}

function ProductRow({
  product,
  onToggle,
}: {
  product: ProductLineItem;
  onToggle: (p: ProductLineItem, selected: boolean) => void;
}) {
  return (
    <ProductRowWrapper onClick={() => onToggle(product, !product.isSelected)}>
      <CircularCheckmark
        square
        size="2rem"
        selected={product.isSelected}
        onChange={(selected) => onToggle(product, selected)}
      />
      <ProductImage src={product?.image} alt={product?.title} />
      <ProductInfo>
        <ProductTitle>{product?.title}</ProductTitle>
        <ProductPrice>${product?.price?.toFixed(2)}</ProductPrice>
      </ProductInfo>
    </ProductRowWrapper>
  );
}

const HiddenTrigger = styled.div`
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
`;

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  align-items: center;
`;

const HeaderTitle = styled.div`
  font-size: 2rem;
  font-weight: 600;
  color: #212529;
  font-family: 'Inter', sans-serif;
  text-align: center;
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-height: 90%;
  display: flex;
  flex-direction: column;
`;

const ProductsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 2rem;
  padding: 0 2.5rem 0;
  flex: 1;
  min-height: 0;
`;

const ProductsScrollContainer = styled.div`
  height: 100%;
  max-height: 40rem;
  overflow-y: auto;
  overflow-x: hidden;
  pointer-events: auto;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 10px;

    &:hover {
      background: #555;
    }
  }
`;

const RulesWrapper = styled.div`
  padding: 0 2.5rem 2rem;

  .qb-row-wrapper {
    margin-top: 2rem;

    div[class*='vsly-small-select'] div[class*='control'] {
      min-width: 40px !important;
      max-width: 190px !important;
    }
  }

  div[class*='query-builder__Row'] {
    gap: 1rem !important;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1.2rem;
  padding: 0 2.5rem;
  &::before {
    content: '';
    position: absolute;
    top: -4rem;
    left: 0;
    right: 0;
    height: 4rem;
    background: linear-gradient(
      to top,
      rgba(255, 255, 255, 0.8) 25%,
      rgba(255, 255, 255, 0.6) 50%,
      rgba(255, 255, 255, 0.35) 75%,
      rgba(255, 255, 255, 0) 100%
    );
    pointer-events: none;
  }
  button {
    min-width: 20rem;
    padding: 1.2rem 5rem !important;
    white-space: nowrap;
  }
`;

const Description = styled.p`
  padding: 0.3rem 2.5rem;
  color: #6c757d;
  font-size: 1.4rem;
  line-height: 1.6;
  margin: auto;
  font-family: 'Inter', sans-serif;
  strong {
    color: #495057;
    font-weight: 600;
  }
`;

const Search = styled.input`
  border-radius: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  border: 1px solid #e9ecef;
  background-color: #fff;
  font-family: Inter, sans-serif;
  padding: 0.8rem 2rem;
  font-size: 1.3rem;

  &:focus {
    outline: none;
    border: solid 0.5px rgba(151, 151, 151, 0.25);
  }
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  padding: 2rem;

  span {
    color: #6c757d;
    font-size: 1.4rem;
    font-family: 'Inter', sans-serif;
  }
`;

const ProductRowWrapper = styled.div`
  display: grid;
  grid-template-columns: 2.5rem 7rem 1fr;
  gap: 1.5rem;
  align-items: center;
  padding: 1.2rem;
  border-radius: 0.8rem;
  transition: background 0.2s;
  cursor: pointer;

  &:hover {
    background: #f8f9fa;
  }
`;

const ProductImage = styled.img`
  width: 7rem;
  height: 7rem;
  object-fit: cover;
  border-radius: 0.6rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const ProductInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
`;

const ProductTitle = styled.div`
  font-size: 1.5rem;
  font-weight: 500;
  color: #212529;
  line-height: 1.4;
  font-family: 'Inter', sans-serif;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const ProductPrice = styled.div`
  font-size: 1.3rem;
  color: #6c757d;
  font-family: 'Inter', sans-serif;
`;
